import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { HotelConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { Child, Room, Travelers } from "src/components/shared/TravelersField/typings";
import { travelersMetadata } from "src/components/shared/TravelersField/utils";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { TypeaheadSelectionProps } from "src/components/flexComponents/WizardHotelPWA/typings";
import { LastSearchData, HotelLastSearchData } from "../../LastSearchData";
import { setCookie } from "src/components/utility/cookie";
import { InfantSeating } from "src/components/shared/TravelersField";
import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";

export class HotelWizardState implements LOBState {
  private globalState: GlobalWizardState;

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public get isDesktop() {
    return this.globalState.isDesktop;
  }

  @computed
  public get useLastSearchForMultiLOB() {
    return this.globalState.config.useLastSearchForMultiLOB;
  }

  public get useLastSearch() {
    return this.useLastSearchForMultiLOB || this.config.lastSearch.useLastSearchForSingleLOB;
  }

  public lastSearchData?: HotelLastSearchData;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @observable
  public config: HotelConfig;

  @observable
  public subLOBState: string = "";

  @observable
  public errorInputRef: React.RefObject<HTMLHeadingElement> | null;

  @observable
  public numberOfErrors = 0;

  @observable
  public errorSummaryRef: React.RefObject<HTMLInputElement> | null;

  public wizardInputsArray: React.RefObject<HTMLInputElement>[] = [];

  //Since this LOB doesn't have a subLOB, setting this as empty so the queryParam is removed when navigating to this LOB
  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = "";
  }

  @computed
  public get travelersValueChanged() {
    return this.globalState.travelersValueChanged;
  }

  @action
  public setTravelersValue = () => {
    this.globalState.setTravelersValue();
  };

  @computed
  public get date() {
    return this.globalState.date;
  }

  @computed
  public get hotelTravelersMetadata() {
    return travelersMetadata(this.travelers.hotel);
  }

  @computed
  public get cruiseTravelersMetadata() {
    return travelersMetadata(this.travelers.cruise);
  }

  @computed
  public get travelers() {
    const nonHotelRoom: [Room] = [
      {
        adults: 2,
        children: [],
        infants: [],
      },
    ];

    // Loop through existing room Info for travelers to find applicable room
    const applicableRoomsForTravelers: Room[] = this.globalState.travelers.hotel.rooms.map((room: Room) => {
      const applicableAdultsPerRoom =
        room.adults > this.config.travelers.maxAdultsPerRoom ? this.config.travelers.maxAdultsPerRoom : room.adults;

      const combinedChildren = room.children.concat(room.infants);
      const applicableChildrenPerRoom =
        combinedChildren.length > this.config.travelers.maxChildrenPerRoom
          ? combinedChildren.splice(0, this.config.travelers.maxChildrenPerRoom)
          : combinedChildren;

      return {
        adults: applicableAdultsPerRoom,
        children: applicableChildrenPerRoom,
        infants: [],
      };
    });

    // Only the first room details to be preserved in nonHotel [Room]
    nonHotelRoom[0] = applicableRoomsForTravelers[0];

    const hotelTravelers = {
      hotel: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotel.infantSeating,
      },
      hotelPackage: {
        rooms: applicableRoomsForTravelers,
        infantSeating: this.globalState.travelers.hotelPackage.infantSeating,
      },
      nonHotel: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.nonHotel.infantSeating,
      },
      cruise: {
        rooms: nonHotelRoom,
        infantSeating: this.globalState.travelers.cruise.infantSeating,
      },
    };

    return hotelTravelers;
  }

  @observable
  public hotelTabSelected: boolean = true;

  @action
  public toggleTabSelected = () => {
    this.hotelTabSelected = !this.hotelTabSelected;
  };

  @observable
  public isMultiplePlacesChecked: boolean = false;

  @action
  public toggleMultiplePlacesCheckbox = () => {
    this.isMultiplePlacesChecked = !this.isMultiplePlacesChecked;
  };

  @observable
  public isAddAFlightChecked: boolean = false;

  @action
  public toggleAddAFlightCheckbox = () => {
    this.isAddAFlightChecked = !this.isAddAFlightChecked;
  };

  @observable
  public isAddACarChecked: boolean = false;

  @action
  public toggleAddACarCheckbox = () => {
    this.isAddACarChecked = !this.isAddACarChecked;
  };

  @computed
  public get hotelPackageType() {
    if (this.isAddAFlightChecked && this.isAddACarChecked) {
      return "fhc";
    } else if (this.isAddAFlightChecked) {
      return "fh";
    } else if (this.isAddACarChecked) {
      return "hc";
    }

    return "";
  }

  @computed
  public get formAction() {
    if (this.isAddAFlightChecked || this.isAddACarChecked) {
      return this.config.form.actionPackage;
    }

    return this.config.form.action;
  }

  // validations
  @observable
  public destinationInvalidKey: string;

  @observable
  public originInvalidKey: string;

  @observable
  public hotelTravelersInvalidKey: string = "";

  @action moreThanOneError = () => {
    return this.globalState.moreThanOneError(this.numberOfErrors);
  };

  @action findFocusElement = (keyword: string) =>
    this.wizardInputsArray.find((element) => element.current !== null && element.current.outerHTML.includes(keyword));

  @action
  public validateDestinationField = () => {
    if (!this.globalState.validateDestinationField()) {
      this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;
      this.errorInputRef = this.findFocusElement("destination")!;

      return false;
    }

    this.destinationInvalidKey = "";

    return true;
  };

  @action
  public validateLessThanNTravelers = () => {
    const isValid = this.globalState.validateLessThanNTravelers(this.hotelTravelersMetadata, this.config.travelers);
    if (!isValid) {
      this.hotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateTravelersField = () => this.validateLessThanNTravelers();

  @observable
  public dateStartInvalidKey: string = "";

  @observable
  public dateEndInvalidKey: string = "";

  @observable
  public advancedOptionsFilterVisible = true;

  @observable
  public checkboxFilterChecks = {
    businessFriendly: false,
    familyFriendly: false,
    freeCancellation: false,
    breakfastIncluded: false,
  };

  @action
  public validateMaxDateRange = () => {
    if (!this.globalState.validateMaxDateRange(this.date.start, this.date.end, this.config.date.maxDaysRange)) {
      this.dateEndInvalidKey = this.config.date.invalidMaxDatesRangeMessageToken;

      if (!this.errorInputRef) {
        this.errorInputRef = this.findFocusElement("d2")! || this.findFocusElement("endDate")!;
      }

      return false;
    }

    return true;
  };

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @action
  public resetValidations = () => {
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.hotelTravelersInvalidKey = "";
  };

  public validateForm = () => {
    this.cleanErrorState();
    const validations: boolean[] = [
      this.validateDestinationField(),
      this.validateEndDateField(),
      this.validateTravelersField(),
    ];

    validations.forEach((value) => !value && (this.numberOfErrors += 1));

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void) {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date) => {
    this.globalState.updateDateSelection(start, end);
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
  };

  @observable
  public sourceIdSelected = "";

  @observable
  public latLong = "";

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection) => {
    const { data } = (selection as TypeaheadSelectionProps) || {};
    const { selected, location } = data || "";
    const { lat, long } = location || "";
    this.sourceIdSelected = selected;
    this.latLong = lat && long ? `${lat},${long}` : "";
    this.globalState.updateDestinationSelection(selection);
    this.destinationInvalidKey = "";
  };

  @action
  public updateOriginSelection = (selection: TypeaheadSelection) => {
    this.globalState.updateOriginSelection(selection);
    this.originInvalidKey = "";
  };

  @action
  public changeFormFilterProperties = (event: React.ChangeEvent<HTMLFormElement>) => {
    const { key: filterKey, checked: isChecked, name: filterName, value: filterValue } = event.currentTarget;
    this.checkboxFilterChecks[filterKey] = isChecked;

    const trackingInfo: FlexTrackingInfo = {
      moduleName: "HOT.SF",
      rfrr: `${filterName}.${filterValue}.${isChecked}`,
      action: Action.CLICK,
      linkName: `${filterName}.${filterValue}`,
    };

    sendDelayedTrackEvent(trackingInfo, this.config.analytics);
  };

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    const rooms = travelers.rooms.map((room) => {
      const [roomInfants, roomChildren] = room.children.reduce(
        ([infants, children]: [Child[], Child[]], child) => {
          child.age < 2 ? infants.push(child) : children.push(child);

          return [infants, children];
        },
        [[], []]
      );

      return {
        adults: room.adults,
        children: roomChildren,
        infants: roomInfants,
      };
    });
    this.globalState.updateHotelTravelersSelection({
      rooms,
      infantSeating: travelers.infantSeating,
    });
    this.hotelTravelersInvalidKey = "";
  };

  @action
  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string,
    destinationID: string,
    hotelID: string
  ) {
    this.globalState.updateDestinationFromCookie(value, regionID, countryCode, latLong, ttla, destinationID, hotelID);
    this.destinationInvalidKey = "";
  }

  public updateDestinationFromContext(context: ExtendedContextStore) {
    const { location } = context?.searchContext;

    if (location?.extendedName) {
      this.updateDestinationFromCookie(
        location.extendedName,
        location.id || "",
        "",
        location.geoCoordinate ? `${location.geoCoordinate.latitude},${location.geoCoordinate.longitude}` : "",
        location.code || "",
        "",
        ""
      );
    }
  }

  @action
  public updateOriginFromCookie(value: string, regionID: string, countryCode: string, latLong: string, ttla: string) {
    this.globalState.updateOriginFromCookie(value, regionID, countryCode, latLong, ttla);
    this.originInvalidKey = "";
  }

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;

    if (this.canTrack && this.useLastSearch && lastSearchData) {
      const destinationValue = lastSearchData.destination?.value
        ? decodeURIComponent(lastSearchData.destination?.value)
        : "";
      const originValue = lastSearchData.flight_origin?.value
        ? decodeURIComponent(lastSearchData.flight_origin?.value)
        : "";

      const startDateFromCookie = lastSearchData.date?.start ? new Date(lastSearchData.date.start) : new Date();
      const endDateFromCookie = lastSearchData.date?.end ? new Date(lastSearchData.date.end) : new Date();

      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;

      this.updateDateSelection(startDateToUse, endDateToUse);

      const hotelTravelers: Travelers = lastSearchData.travelers
        ? {
            rooms: JSON.parse(lastSearchData.travelers),
            infantSeating: lastSearchData.infantSeating === "seat" ? InfantSeating.IN_SEAT : InfantSeating.ON_LAP,
          }
        : this.travelers.hotel;

      this.updateHotelTravelersSelection(hotelTravelers);

      this.updateDestinationFromCookie(
        destinationValue !== "undefined" ? destinationValue : "",
        lastSearchData.destination?.regionID ? lastSearchData.destination.regionID : "",
        lastSearchData.destination?.countryCode ? lastSearchData.destination.countryCode : "",
        lastSearchData.destination?.latLong ? lastSearchData.destination.latLong : "",
        lastSearchData.destination?.ttla ? lastSearchData.destination.ttla : "",
        lastSearchData.destination?.destinationID ? lastSearchData.destination.destinationID : "",
        lastSearchData.destination?.hotelID ? lastSearchData.destination.hotelID : ""
      );

      this.isMultiplePlacesChecked = lastSearchData.multiplePlacesToStay
        ? JSON.parse(lastSearchData.multiplePlacesToStay)
        : this.isMultiplePlacesChecked;

      this.isAddACarChecked = lastSearchData.addACar ? JSON.parse(lastSearchData.addACar) : this.isAddACarChecked;

      this.isAddAFlightChecked = lastSearchData.addAFlight
        ? JSON.parse(lastSearchData.addAFlight)
        : this.isAddAFlightChecked;

      if (this.isAddAFlightChecked) {
        this.updateOriginFromCookie(
          originValue && originValue !== "undefined" ? originValue : "",
          lastSearchData.flight_origin?.regionID ? lastSearchData.flight_origin.regionID : "",
          lastSearchData.flight_origin?.countryCode ? lastSearchData.flight_origin.countryCode : "",
          lastSearchData.flight_origin?.latLong ? lastSearchData.flight_origin.latLong : "",
          lastSearchData.flight_origin?.ttla ? lastSearchData.flight_origin.ttla : ""
        );
      }
    }
  };

  public setLastSearchCookie = () => {
    if (this.canTrack && this.useLastSearch) {
      const destination = this.location.destination;
      const destinationMetaData = this.location.destination.metaData;
      const date = this.date;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        hotel: {
          destination: {
            value: destination.value || "",
            regionID: destinationMetaData.regionId || "",
            ttla: destinationMetaData.ttla || "",
            countryCode: destinationMetaData.countryCode || "",
            latLong: destinationMetaData.latLong || "",
            destinationID: destinationMetaData.destinationId || "",
            hotelID: destinationMetaData.hotelId || "",
          },
          date: {
            start: date.start ? date.start.toString() : "",
            end: date.end ? date.end.toString() : "",
          },
          travelers: JSON.stringify(this.travelers.hotel.rooms),
          infantSeating: this.travelers.hotel.infantSeating,
          multiplePlacesToStay: this.isMultiplePlacesChecked.toString(),
          addACar: this.isAddACarChecked.toString(),
          addAFlight: this.isAddAFlightChecked.toString(),
        },
      };

      //only add flight last search data to the cookie if needed
      if (this.isAddAFlightChecked) {
        lastSearchCookieObject.hotel!.flight_origin = {};
        lastSearchCookieObject.hotel!.flight_origin.value = this.isAddAFlightChecked ? this.location.origin.value : "";
        lastSearchCookieObject.hotel!.flight_origin.regionID = this.isAddAFlightChecked
          ? this.location.origin.metaData.regionId
          : "";
        lastSearchCookieObject.hotel!.flight_origin.ttla = this.isAddAFlightChecked
          ? this.location.origin.metaData.ttla
          : "";
        lastSearchCookieObject.hotel!.flight_origin.countryCode = this.isAddAFlightChecked
          ? this.location.origin.metaData.countryCode
          : "";
        lastSearchCookieObject.hotel!.flight_origin.latLong = this.isAddAFlightChecked
          ? this.location.origin.metaData.latLong
          : "";
      }

      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject),
      });
    }
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
      this.errorSummaryRef = this.findFocusElement("error")!;
      this.errorSummaryRef?.current?.focus();
    } else if (this.canTrack && this.useLastSearch) {
      this.setLastSearchCookie();
    }
  };

  /**
   * Cleans error state in all forms
   */

  @action
  public cleanErrorState = () => {
    if (this.errorInputRef) {
      this.errorInputRef = null;
    }
    this.numberOfErrors = 0;
  };

  public updateDateFromConfig = () => {
    this.globalState.updateDateFromConfig(this.config.date);
  };

  constructor(config: HotelConfig, globalState: GlobalWizardState, lastSearchData?: LastSearchData) {
    this.config = config;
    this.globalState = globalState;
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.hotel;
    }
  }
}
